"use client";

import { IconCircle } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Rating } from "@/components/common/rating";
import { ListingItemCateringImage } from "@/components/listing/listing-item-catering-image";
import { ListingItemImage } from "@/components/listing/listing-item-image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { DietListItemOmnibusPrice } from "@/features/diets/components/diet-list-item/diet-list-item-omnibus-price";
import { DietListItemOrderButton } from "@/features/diets/components/diet-list-item/diet-list-item-order-button";
import { DietListItemPricing } from "@/features/diets/components/diet-list-item/diet-list-item-pricing";
import { DietMenuModal } from "@/features/diets/components/diet-menu-modal/diet-menu-modal";
import { DiscountCodes } from "@/features/diets/components/discount-codes";
import {
  getDietCardOmnibus,
  isOmnibusEnabled,
} from "@/features/diets/helpers/omnibus";
import type { DietCatalog } from "@/features/diets/types";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";

export const DietCatalogCarouselItem = ({
  diet,
  showMenu = false,
}: {
  diet: DietCatalog;
  showMenu?: boolean;
}) => {
  const t = useTranslations();
  // Omnibus (MPL-1835): the same price as the listing card; badges stay as is.
  const omnibusEnabled = isOmnibusEnabled();
  const omnibus = getDietCardOmnibus({ diet });
  const configParams = {
    configuration: diet.searchResultId || diet.configurationId || "",
  };

  return (
    <Card className="relative flex h-full flex-col overflow-hidden rounded-xl border border-brand-grey2 bg-brand-white shadow-none">
      <div className="relative h-[150px]">
        <ListingItemImage
          className="h-full w-full rounded-none! md:w-full"
          imageClassName="h-full w-full !rounded-none"
          imageUrl={diet.imageUrl}
          name={formatDietName(diet.name)}
        />
        <ListingItemCateringImage
          className="absolute top-2 left-2 max-md:h-12 max-md:w-12"
          imageUrl={diet.catering.logo.url}
          name={formatCateringName(diet.catering.name)}
        />
        <div className="absolute bottom-2 left-2">
          <Rating
            className="bg-brand-white"
            count={diet.rating.count}
            details={false}
            isTrending={diet.rating.isTrending}
            productId={diet.id}
            productName={formatDietName(diet.name)}
            rating={diet.rating.value}
            size="md"
          />
        </div>
      </div>

      <CardContent className="flex min-h-64 flex-1 flex-col gap-3 p-4">
        <ScrollArea className="w-full overflow-x-auto">
          {(diet.tags.length > 0 || diet.discountCodes) && (
            <div className="flex flex-nowrap gap-1.5">
              <DiscountCodes
                className="order-2"
                discountCodes={diet.discountCodes}
              />
              {diet.tags.map((tag, index) => (
                <Badge
                  className="order-5 rounded-full border-0 bg-brand-brown/10 px-3 py-2 text-brand-brown md:px-2 md:py-1.5"
                  key={tag.slug + index.toString()}
                  variant="outline"
                >
                  <p className="flex items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-sm leading-4 md:text-[11px]">
                    {t(`dietTags.${tag.slug}`)}
                  </p>
                </Badge>
              ))}
            </div>
          )}
          <ScrollBar className="hidden" orientation="horizontal" />
        </ScrollArea>

        <h3 className="line-clamp-2 font-medium text-brand-green2-300 text-xl">
          <span>{formatDietName(diet.name)}</span>
          <IconCircle
            className="mx-2 inline-block h-1.5 w-1.5 rounded-full border border-brand-grey5"
            stroke={1.25}
          />
          <span>{formatCateringName(diet.catering.name)}</span>
        </h3>

        <div className="mt-auto">
          {omnibusEnabled ? (
            <DietListItemOmnibusPrice
              lowestPrice={omnibus.lowestPrice}
              price={diet.basePrice}
              promoPrice={omnibus.promoPrice}
            />
          ) : (
            <DietListItemPricing
              discountCodes={diet.discountCodes}
              price={diet.basePrice}
            />
          )}
          <div className="mt-5 flex flex-1 gap-2">
            {showMenu && (
              <DietMenuModal
                dietId={diet.id}
                kcal={diet.calorifics.at(0)?.calories.toString()}
                omnibusPrices={omnibus}
                trigger={
                  <Button
                    className="h-12 min-h-0 w-full rounded-lg border-brand-green2-300 px-0 font-medium text-brand-green2-300 md:h-10 md:px-3 xl:h-12"
                    variant="outline"
                  >
                    {t("diets.check_menu")}
                  </Button>
                }
              />
            )}
            <DietListItemOrderButton configParams={configParams} diet={diet} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
